import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { TbTriangleFilled } from 'react-icons/tb';
import {
  tableAnimVariants,
  tableRowAnimVariants,
} from '../../../constants/animationVariants';
import { sortByDate, sortByTime } from '../../../utils/timeAndDate';
import AcademiaSearchInput from '../Inputs/AcademiaSearchInput';
import AcademiaLoading from '../Loadings/AcademiaLoading';
import NoTableDataFound from '../NoTableDataFound';
import Pagination from '../Paginations/Pagination';

const AcademiaTable = ({
  data,
  columns,
  notFoundMessage,
  tableToolbarComponent,
  hasMultipleToolbarComponents,
  isTableDataLoading,
}: {
  data: Array<any>;
  columns: any;
  notFoundMessage: string;
  tableToolbarComponent?: React.ReactNode;
  hasMultipleToolbarComponents?: boolean;
  isTableDataLoading?: boolean;
}) => {
  // integration of react hooks here
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  });
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchText, setSearchText] = useState<string>('');

  // integration of tanstack table hooks here
  const table = useReactTable({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    state: {
      pagination,
      sorting,
    },
    sortingFns: {
      sortByDate,
      sortByTime,
    },
  });

  // setting table data here
  useEffect(() => {
    setTableData(data);

    if (searchText) {
      setPagination((state) => ({ ...state, pageIndex: 0 }));

      const searchResults = data.filter((d) =>
        Object.values(d)
          .map((val) => (val + '').toLowerCase())
          .some((val) => val.includes(searchText.toLowerCase()))
      );

      setTableData(searchResults);
    }
  }, [data, searchText]);

  // rendering custom table component here
  return (
    <motion.div variants={tableAnimVariants} initial='hidden' animate='visible'>
      <motion.div
        variants={tableRowAnimVariants}
        initial='hidden'
        animate='visible'
        className='md:px-3'
      >
        <div
          className={`p-3 flex flex-col gap-3 items-center justify-between bg-base-white shadow-academia-sm rounded-xl ${
            hasMultipleToolbarComponents ? 'xl:flex-row' : 'md:flex-row'
          }`}
        >
          <div
            className={`w-1/5 min-w-full ${
              hasMultipleToolbarComponents
                ? 'xl:min-w-[370px]'
                : 'md:min-w-[370px]'
            }`}
          >
            <AcademiaSearchInput
              searchText={searchText}
              setSearchText={setSearchText}
            />
          </div>

          {tableToolbarComponent && tableToolbarComponent}
        </div>
      </motion.div>

      <div className='px-3 overflow-x-auto overflow-y-hidden'>
        {isTableDataLoading ? (
          <AcademiaLoading />
        ) : !table.getRowCount() ? (
          <NoTableDataFound message={notFoundMessage} />
        ) : (
          <table className='w-full min-w-[500px] border-separate border-spacing-y-3'>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <motion.tr
                  key={headerGroup.id}
                  variants={tableRowAnimVariants}
                  initial='hidden'
                  animate='visible'
                  className='bg-legal-dark-blue drop-shadow-academia-sm'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      title={
                        header.column.getCanSort()
                          ? header.column.getNextSortingOrder() === 'asc'
                            ? 'Sort Ascending'
                            : header.column.getNextSortingOrder() === 'desc'
                            ? 'Sort Descending'
                            : 'Clear Sorting'
                          : undefined
                      }
                      className={`text-legal-brown py-3 font-semibold text-lg first:rounded-l-xl last:rounded-r-xl ${
                        header.column.getCanSort()
                          ? 'cursor-pointer hover:opacity-70 duration-300'
                          : ''
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className='group relative'>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}

                        <span
                          className={`absolute top-1/2 -translate-y-1/2 duration-300 ${
                            header.column.getCanSort() &&
                            'group-hover:opacity-100'
                          } ${
                            header.column.getIsSorted() === 'asc'
                              ? 'rotate-0'
                              : header.column.getIsSorted() === 'desc'
                              ? 'rotate-180'
                              : 'opacity-0'
                          }`}
                        >
                          <TbTriangleFilled className='size-4' />
                        </span>
                      </div>
                    </th>
                  ))}
                </motion.tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <motion.tr
                  key={row.id}
                  variants={tableRowAnimVariants}
                  initial='hidden'
                  animate='visible'
                  className='text-center drop-shadow-academia-sm'
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      title={
                        typeof cell.getValue() === 'string' ||
                        typeof cell.getValue() === 'number'
                          ? (cell.getValue() as string)
                          : undefined
                      }
                      key={cell.id}
                      className='px-2 py-3 max-w-[150px] first:rounded-l-xl last:rounded-r-xl bg-base-white text-legal-dark-blue'
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {table.getRowCount() && !isTableDataLoading ? (
        <motion.div
          variants={tableRowAnimVariants}
          initial='hidden'
          animate='visible'
          className='md:px-3'
        >
          <Pagination
            table={table}
            currentPage={pagination.pageIndex}
            rowsPerPage={pagination.pageSize}
          />
        </motion.div>
      ) : null}
    </motion.div>
  );
};

export default AcademiaTable;
